import type { VariantProps } from 'class-variance-authority'
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  Key,
  ReactElement,
  ReactNode,
  Ref,
} from 'react'
import type { buttonVariants } from './styles'

/**
 * Button size variants
 */
export const ButtonSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const

export type ButtonSize = (typeof ButtonSize)[keyof typeof ButtonSize]

/**
 * Container widths (Tailwind container sizes, 384-768px) below which a
 * CollapsibleButton shows only its icon
 */
export const ButtonCollapseBelow = {
  Small: 'sm',
  Medium: 'md',
  Large: 'lg',
  XL: 'xl',
  XXL: '2xl',
  XXXL: '3xl',
} as const

export type ButtonCollapseBelow =
  (typeof ButtonCollapseBelow)[keyof typeof ButtonCollapseBelow]

/**
 * Button style variants
 */
export const ButtonVariant = {
  Primary: 'primary',
  Secondary: 'secondary',
  Destructive: 'destructive',
  Success: 'success',
  Ghost: 'ghost',
} as const

export type ButtonVariant = (typeof ButtonVariant)[keyof typeof ButtonVariant]

interface BaseButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as a different element using Base UI's render prop */
  render?: ReactElement
  ref?: Ref<HTMLButtonElement>
}

/**
 * Content props with accessibility enforcement.
 *
 * - Button with text: no aria-label required
 * - Button without text (icon-only): must have aria-label or aria-labelledby
 */
type ButtonContentProps =
  | { children: ReactNode; 'aria-label'?: string; 'aria-labelledby'?: string }
  | { children?: never; 'aria-label': string; 'aria-labelledby'?: string }
  | { children?: never; 'aria-label'?: string; 'aria-labelledby': string }

/**
 * Public Button props
 */
export type ButtonProps = BaseButtonProps &
  ButtonContentProps & {
    /** Content rendered before children (icons, indicators, etc.) */
    leftSection?: ReactNode
    /** Content rendered after children (icons, badges, etc.) */
    rightSection?: ReactNode
    /** Button style variant */
    variant: ButtonVariant
    /** Shows spinner and disables button */
    isLoading?: boolean
    /**
     * Keep the button focusable while disabled: renders `aria-disabled`
     * instead of the native `disabled` attribute (activation is still
     * blocked). Hover and focus events keep firing, so a wrapping Tooltip
     * can open - the pattern for explaining why a button is disabled.
     */
    focusableWhenDisabled?: boolean
    /**
     * Disables the button and explains why: the reason opens as a tooltip on
     * hover and keyboard focus, and becomes the button's accessible
     * description. Implies `focusableWhenDisabled`. Pass it on every render,
     * as `undefined` while enabled, so the button keeps focus when it changes.
     */
    disabledReason?: ReactNode
  }

/**
 * @internal Props passed by AnimatedButton. Not for direct use.
 */
export type InternalButtonProps = ButtonProps & {
  /** @internal Key for label element - triggers remount for fade animation */
  labelKey?: Key
  /** @internal Style for label element - fade-in animation CSS */
  labelStyle?: CSSProperties
}
